import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

interface Merchant {
  DSSN: string;
  business_name: string;
  merchant_id: number;
  email: string;
  phone_number: string;
  status?: string;
}

interface AuthContextType {
  token: string | null;
  merchant: Merchant | null;
  login: (dssn: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('merchant_token'));
  const [merchant, setMerchant] = useState<Merchant | null>(() => {
    const savedMerchant = localStorage.getItem('merchant_data');
    return savedMerchant ? JSON.parse(savedMerchant) : null;
  });

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const login = async (dssn: string, password: string) => {
    try {
      console.log('Attempting login for DSSN:', dssn);
      
      const response = await axios.post(`${API_URL}/merchants/login`, {
        dssn,
        password
      });

      console.log('Login response:', response.data);

      if (response.data.success) {
        const { token: authToken, merchant: merchantData } = response.data;
        
        setToken(authToken);
        setMerchant(merchantData);
        
        localStorage.setItem('merchant_token', authToken);
        localStorage.setItem('merchant_data', JSON.stringify(merchantData));
        
        console.log('Login successful for:', merchantData.business_name);
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error.response?.data || error.message);
      
      // Handle specific error cases
      if (error.response?.status === 403 && error.response?.data?.requires_password_setup) {
        throw new Error('Please set up your password first. Use the "First time?" link.');
      }
      
      if (error.response?.status === 403 && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      
      if (error.response?.status === 401) {
        throw new Error('Invalid credentials. Please check your DSSN and password.');
      }
      
      throw new Error(error.response?.data?.error || 'Login failed. Please check your credentials.');
    }
  };

  const logout = () => {
    setToken(null);
    setMerchant(null);
    localStorage.removeItem('merchant_token');
    localStorage.removeItem('merchant_data');
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        merchant,
        login,
        logout,
        isAuthenticated: !!token
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
